import type { JSX } from 'react';
import { Link, Navigate } from 'react-router';
import { Badge, Button, Card, FollowersIcon } from '@viniciomendesr/rayrovisk-design-system';
import { useProfile } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { PromptBody } from '../components/PromptBody';

const EXAMPLE = `Você é roteirista de Reels para Instagram e escreve com a voz de quem assina o vídeo.

## PERFIL DE QUEM FALA
Nicho: confeitaria artesanal sob encomenda, em São Paulo.
Fala para: mulheres de 28 a 45 anos que compram bolo para festa de família.
Vende: bolos e doces por encomenda, de R$ 180 a R$ 400.
Palavras que ela evita: amores, gente, bombar, insano.

## TAREFA
Roteiro de Reels contando por que ela parou de fazer bolo de festa padrão
e passou a aceitar só encomenda autoral.

## FORMATO DA RESPOSTA
Gancho, três blocos numerados com o tempo estimado, e o fechamento.`;

/**
 * Screen 06, and the screen a buyer of the generator lands on first. It exists
 * because the alternative is opening onto an empty form: seeing the output
 * before filling anything is what makes four minutes of onboarding worth it.
 */
export function GeneratorIntroScreen(): JSX.Element {
  const profile = useProfile();

  const state = accessStateOf(profile.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  // Already has one: this screen has nothing left to explain.
  if (profile.isSuccess && profile.data.profile !== null) {
    return <Navigate to="/gerador/montar" replace />;
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 26, maxWidth: 1180 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: '62ch' }}>
        <span
          style={{
            font: '600 10px/1 var(--rvk-font-functional)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          {/* Not "ao Gerador de Roteiros Inteligentes" any more. From
              2026-08-06 this is where every first login lands, and naming the R$
              297 product would tell a R$ 37 buyer she is looking at something
              she did not buy. */}
          Primeiro acesso
        </span>
        <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)' }}>
          Antes de você preencher nada, veja o que sai.
        </h1>
        <p
          style={{
            margin: 0,
            font: '400 14.5px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Este é um perfil fictício, uma tarefa de uma linha e o prompt que a ferramenta monta a
          partir dos dois. Nada aqui foi editado à mão.
        </p>
      </div>

      <Card layer="functional" elevation="raised" padding="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge tone="neutral" variant="outline" size="sm">
              Perfil de exemplo
            </Badge>
            <span
              style={{
                font: '400 13px/1.5 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              “preciso de um reels contando por que eu mudei de nicho”
            </span>
          </div>
          <PromptBody text={EXAMPLE} />
        </div>
      </Card>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ font: '400 19px/1.2 var(--rvk-font-expressive)' }}>Agora com a sua voz.</span>
          <span
            style={{
              font: '400 13px/1.5 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)'
            }}
          >
            Quatro telas, e nenhuma com mais de cinco campos obrigatórios. Restrições você pode
            pular e voltar depois, e o resto da plataforma continua ali no menu se você preferir
            começar por ele.
          </span>
        </div>
        <Link to="/gerador/perfil">
          <Button variant="primary" size="lg" iconStart={<FollowersIcon size={16} />}>
            Montar o meu perfil
          </Button>
        </Link>
      </div>
    </section>
  );
}
